import {
    api,
    apiErrorMessage,
    formatDay,
    useAuthStore,
    useIsPlatformHost,
} from '@bemor/web-kit';
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    DateTime,
    PageHeader,
    RowActions,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { RiskModel, RiskModelDetail } from '@/api/types';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';

const PATH = '/staff/risk/models';

/** A model's version is its export date (YYYY-MM-DD in the API): shown as a day, never an ISO fragment (UI rule 0.2). */
export const versionLabel = (v: string): string =>
    /^\d{4}-\d{2}-\d{2}$/.test(v) ? formatDay(v) : v;

/** BM-158 QA: a row menu's name; a day has a tenant and a platform model, so the scope keeps the two apart. */
export const modelMenuName = (m: { scope: string; version: string }): string =>
    `${m.scope} model of ${versionLabel(m.version)}`;

/** A metric as the trainer measured it, three decimals; "—" before the first validation. */
export const metricLabel = (v: number | null | undefined): string =>
    v == null ? '—' : v.toFixed(3);

/** What a model's state means on the page (decision 45). */
export const MODEL_STATES = {
    candidate: ['warning', 'Candidate'],
    active: ['success', 'Active'],
    retired: ['neutral', 'Retired'],
    invalid: ['danger', 'Invalid'],
} as const;

/** The actions a row offers: a candidate activates (own scope on this host), an active or candidate one retires. */
export function modelActions(
    m: Pick<RiskModel, 'state' | 'scope'>,
    platformHost: boolean,
): ('activate' | 'retire')[] {
    const ownScope = platformHost
        ? m.scope === 'platform'
        : m.scope === 'tenant';
    if (!ownScope) return [];
    const out: ('activate' | 'retire')[] = [];
    if (m.state === 'candidate') out.push('activate');
    if (m.state === 'candidate' || m.state === 'active') out.push('retire');
    return out;
}

function ModelDetailCard({ id, onClose }: { id: string; onClose: () => void }) {
    const detail = useOne<RiskModelDetail>(`${PATH}/${id}`);
    const d = detail.data?.data;
    return (
        <Card data-testid="risk-model-detail">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">
                    {d
                        ? `${d.scope} model of ${versionLabel(d.version)}`
                        : 'Model'}{' '}
                    {d ? <Badge variant="outline">{d.algorithm}</Badge> : null}
                </CardTitle>
                <Button size="sm" variant="outline" onClick={onClose}>
                    Close
                </Button>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                {d ? (
                    <>
                        <p className="m-0">
                            Trained <DateTime value={d.trained_at} /> on{' '}
                            {d.rows_fraud} fraud and {d.rows_good} good rows,{' '}
                            {d.metrics.trees ?? '—'} trees over{' '}
                            {d.features.length} features. AP{' '}
                            {metricLabel(d.metrics.ap)}, precision at{' '}
                            {d.metrics.k ?? '—'} a day{' '}
                            {metricLabel(d.metrics.cp_at_k)}.
                        </p>
                        {d.error ? (
                            <p className="text-destructive m-0">{d.error}</p>
                        ) : null}
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Feature</TableHead>
                                    <TableHead className="text-right">
                                        Gain
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {d.top_features.map((f) => (
                                    <TableRow key={f.feature}>
                                        <TableCell className="font-mono text-xs">
                                            {f.feature}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {f.gain.toFixed(1)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                ) : (
                    <p className="text-fg-2 m-0">Loading…</p>
                )}
            </CardContent>
        </Card>
    );
}

/** BM-125 decisions 15, 45, 48: Risk → Models. Every trained model is a candidate until staff activate it here. */
export function RiskModelsPage() {
    const can = useAuthStore((s) => s.can);
    const platformHost = useIsPlatformHost();
    const models = useOne<RiskModel[]>(PATH);
    const [open, setOpen] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<{
        id: string;
        action: 'activate' | 'retire';
    } | null>(null);
    const act = useWrite(
        async ({ id, action }: { id: string; action: 'activate' | 'retire' }) =>
            (
                await api().post<RiskModel>(
                    `${PATH}/${id}/${action}`,
                    {},
                    { idempotencyKey: crypto.randomUUID() },
                )
            ).data,
        [PATH, '/staff/risk/dashboard'],
    );
    const train = useWrite(
        async () =>
            (
                await api().post<{ queued: boolean }>(
                    `${PATH}/train`,
                    {},
                    { idempotencyKey: crypto.randomUUID() },
                )
            ).data,
        [PATH],
    );
    const rows = models.data?.data ?? [];
    return (
        <div>
            <PageHeader
                title="Risk models"
                meta="The nightly trainer stores every model as a candidate with its figures; one activates per scope and the checks score with it (the tenant's own, else the platform's)."
                primaryAction={
                    can(P.riskModelsManage) ? (
                        <Button
                            disabled={train.isPending}
                            onClick={() => train.mutate(undefined)}
                        >
                            {train.isSuccess ? 'Export queued' : 'Train now'}
                        </Button>
                    ) : undefined
                }
            />
            {train.error ? (
                <p className="text-destructive text-sm">
                    {apiErrorMessage(train.error)}
                </p>
            ) : null}
            <div className="space-y-4" data-testid="risk-models">
                {models.data && rows.length === 0 ? (
                    <p className="text-fg-2 text-sm">
                        No model yet: the trainer writes the first candidate
                        after the nightly export.
                    </p>
                ) : null}
                {rows.length ? (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Version</TableHead>
                                    <TableHead>Scope</TableHead>
                                    <TableHead>Trained</TableHead>
                                    <TableHead className="text-right">
                                        Rows (fraud / good)
                                    </TableHead>
                                    <TableHead className="text-right">
                                        AP
                                    </TableHead>
                                    <TableHead className="text-right">
                                        Precision at k
                                    </TableHead>
                                    <TableHead>State</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((m) => (
                                    <TableRow
                                        key={m.id}
                                        data-testid={`risk-model-${m.scope}`}
                                    >
                                        <TableCell className="font-mono text-xs">
                                            {versionLabel(m.version)}
                                        </TableCell>
                                        <TableCell>{m.scope}</TableCell>
                                        <TableCell>
                                            <DateTime
                                                value={m.trained_at}
                                                format="date"
                                            />
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {m.rows_fraud} / {m.rows_good}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {metricLabel(m.metrics.ap)}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {metricLabel(m.metrics.cp_at_k)}
                                            {m.metrics.k
                                                ? ` (k ${m.metrics.k})`
                                                : ''}
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge
                                                status={m.state}
                                                domain={MODEL_STATES}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <RowActions
                                                name={modelMenuName(m)}
                                                items={[
                                                    {
                                                        label: 'Details',
                                                        onSelect: () =>
                                                            setOpen(m.id),
                                                    },
                                                    ...(can(P.riskModelsManage)
                                                        ? modelActions(
                                                              m,
                                                              platformHost,
                                                          ).map((action) => ({
                                                              key: action,
                                                              label:
                                                                  action ===
                                                                  'activate'
                                                                      ? 'Activate'
                                                                      : 'Retire',
                                                              destructive:
                                                                  action ===
                                                                  'retire',
                                                              onSelect: () =>
                                                                  setConfirm({
                                                                      id: m.id,
                                                                      action,
                                                                  }),
                                                          }))
                                                        : []),
                                                ]}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : null}
                {open ? (
                    <ModelDetailCard id={open} onClose={() => setOpen(null)} />
                ) : null}
            </div>
            {confirm ? (
                <ConfirmDialog
                    open
                    onOpenChange={(o) => (o ? null : setConfirm(null))}
                    title={
                        confirm.action === 'activate'
                            ? 'Activate this model'
                            : 'Retire this model'
                    }
                    // BM-158 self-review: the body is the dialog's <p> description, so it takes text, never a <p>
                    body={
                        confirm.action === 'activate'
                            ? 'The checks score with it from now; the model active in this scope is retired. Audited.'
                            : 'The checks fall back to the platform model, or read no score until another is activated. Audited.'
                    }
                    confirmLabel={
                        confirm.action === 'activate' ? 'Activate' : 'Retire'
                    }
                    destructive={confirm.action === 'retire'}
                    busy={act.isPending}
                    error={act.error ? apiErrorMessage(act.error) : undefined}
                    onConfirm={() =>
                        act.mutate(confirm, {
                            onSuccess: () => setConfirm(null),
                        })
                    }
                />
            ) : null}
        </div>
    );
}
